<template>
  <div
    class="d-flex justify-content-center mx-xs">
    <div class="share-content">
      <div
        v-if="editing"
        class="mx-xs my-xxs">
        <div class="d-flex align-items-center gap-xs">
          <span class="text-gray text-nowrap semi-bold">
            armoire.style/refer/
          </span>
          <base-input
            ref="link-input"
            v-model="editCode"
            :validations="v$.editCode"
            :read-only="showConfirmation"
            class="input"
            @update:modelValue="v$.$touch()"/>
        </div>
        <BaseButton
          class="w-100 mt-xs"
          :disabled="(v$.$error && !v$.$pending) || showConfirmation"
          variant="primary"
          :text="showConfirmation ? 'Updating...' : 'Update link'"
          @click="handlePrimaryClick">
          <template #default>
            <div
              class="d-flex align-items-center justify-content-center"
              :class="{'scale': showConfirmation}">
              <svg-check
                v-if="showConfirmation"
                class="check scale"
                height="16"
                width="16"/>
            </div>
          </template>
        </BaseButton>
        <BaseButton
          v-if="!showConfirmation"
          class="w-100 mt-xs"
          variant="text-link"
          text="Cancel"
          @click="customizeLink"/>
      </div>
      <CopyLink
        v-else
        highlight
        show-customize-link-button
        prefix="armoire.style/refer/"
        :full-link="link"
        :display-link="referralCode"
        @copy-link-click="trackLinkCopied"
        @customize-link-click="customizeLink"/>
    </div>
  </div>
</template>

<script>
import BaseInput from '../global/BaseInput'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import { mapActions } from 'vuex'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, helpers } from '@vuelidate/validators'
import { validReferralCode } from '../global/vuelidate/customValidators'
import useAnalytics from '@shared/composables/analytics.js'
import CopyLink from '../global/sequin/CopyLink'
import SvgCheck from '@/components/global/svg/SvgCheck.vue'

export default {
  name: 'ReferralShareLink',
  components: {
    CopyLink,
    BaseInput,
    BaseButton,
    SvgCheck,
  },
  props: {
    referralCode: {
      type: String,
      required: true
    }
  },
  setup () {
    const { track } = useAnalytics()

    return {
      track,
      v$: useVuelidate()
    }
  },
  data () {
    return {
      editing: false,
      editCode: this.referralCode,
      showConfirmation: false,
      prefix: 'armoire.style/refer/'
    }
  },
  computed: {
    link () {
      return `https://armoire.style/refer/${this.referralCode}`
    }
  },
  methods: {
    ...mapActions('client', [
      'checkReferralCodeAvailability',
      'updateReferralCode'
    ]),
    handlePrimaryClick () {
      this.updateLink()
      setTimeout(() => {
        this.showConfirmation = false
      }, 5000)
    },
    customizeLink () {
      this.v$.$reset()
      this.editCode = this.referralCode
      this.showConfirmation = false
      this.editing = !this.editing
    },
    async updateLink () {
      if (this.editCode !== this.referralCode) {
        await this.updateReferralCode(this.editCode)
      }
      this.showConfirmation = true
      setTimeout(() => {
        this.editing = false
      }, 3000)
    },
    trackLinkCopied () {
      this.track('Shared Referral - Copy Link')
    }
  },
  validations: {
    editCode: {
      required,
      minLength: minLength(3),
      validReferralCode,
      uniqueReferralCode: helpers.withAsync(async function (value) {
        if (value === '' || value.length < 3) return true
        const res = await this.checkReferralCodeAvailability(value)
        return res.available
      })
    }
  }
}
</script>

<style lang="scss" scoped>


  .share-content {
    max-width: 400px;
    width: 100%;
  }

  .scale {
    @include scale-up-down-animation;
  }

  .input {
    width: 100%;
    margin: 10px 0 !important;
    &.highlight :read-only {
      color: $plum;
      font-weight: $font-weight-semibold;
      background-color: white;
    }
  }

  .check {
    stroke-width: 4px;
    margin-left: 6px;

    transform-origin: 50% 50%;
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    animation: stroke .2s ease-in .4s forwards;

    @keyframes stroke {
      100% {
        stroke-dashoffset: 48;
      }
    }
  }
</style>
